


































































































































































































































































































































import { computed, defineComponent, nextTick, PropType, ref, watch } from '@nuxtjs/composition-api';
import { merge } from 'lodash-es';
import { useFacets, MappedAggregation, isFilterableFacet } from '~/features/facets';
import { usePagination } from '~/features/pagination';
import { ProductListingType, useProductsGeneric } from '~/features/products';
import {
  ProductAttributeSortInput,
  SortEnum,
  AggregationOption,
  ProductAttributeFilterInput,
} from '~/graphql-types.gen';
import { OfferCardFragment } from '~/graphql/fragments';
import { ProductsQuery, ProductsQueryVariables } from '~/graphql/Product';

function disabledFacet(facet: MappedAggregation, excluded?: string[]): boolean {
  if (!excluded?.length) {
    return false;
  }

  return excluded?.includes(facet.code);
}

function enabledFacet(facet: MappedAggregation, included?: string[]): boolean {
  // If no preferable facets, then return them all

  return !included?.length ? true : included.includes(facet.code);
}

const options = {
  enableFacetTags: true,
};

export default defineComponent({
  props: {
    /*
     * The type of product listing to display (default , discounted or search).
     * @property type
     * @type ProductListingType
     * @public
     * @required
     * @default ProductListingType.default
     */
    type: {
      type: String as PropType<ProductListingType>,
      default: 'default',
      required: false,
      validator: (value: string) => ['default', 'discounted', 'search', 'isNew'].includes(value),
    },

    value: {
      type: Array as PropType<MappedAggregation[]>,
      default: () => [],
      required: false,
    },
    /**
     * contains the search phrase that was used to filter the products
     * in case of a search listing
     */
    search: {
      type: String,
      default: '',
      required: false,
    },
    /**
     * contains the brand used to filter the products
     * in case of a brand listing
     */
    brandId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    /**
     * contains the category used to filter the products
     * in case of a category listing
     */
    categoryId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    /**
     * Contains the email of the seller
     */
    seller: {
      type: String,
      default: '',
      required: false,
    },
    /**
     *
     * contains a list of excluded facets from being displayed in the facet dialog or facet dropdown
     */
    renderExcludedFacets: {
      type: Array as PropType<string[]>,
      default: () => [],
      required: false,
    },
    /**
     *
     * contains a list of Preferable facets that will be displayed in the facet dialog or facet dropdown
     * *used only during rendering the options of the facet dropdown
     */
    renderIncludedFacets: {
      type: Array as PropType<string[]>,
      default: () => [],
      required: false,
    },
    /**
     *
     * contains a list of Preferable facets that should be populated to the the component when it is mounted
     * ! this is so important in case of there is a large list of facets
     */
    includedFacets: {
      type: Array as PropType<string[]>,
      default: () => [],
      required: false,
    },

    /**
     *
     * filters
     */
    filters: {
      type: Object as PropType<ProductAttributeFilterInput>,
      default: () => {},
    },
    /**
     * An offer banner to be placed as a first item in listing if it exists
     */
    listingAds: {
      type: Array as PropType<OfferCardFragment[]>,
      default: () => [],
    },
  },
  setup(props, { emit, slots }) {
    const isFiltering = ref(false);
    const data = ref<ProductsQuery | null>(null);

    const PAGE_SIZE_MAPPER: { [key: number]: number } = {
      0: 12,
      1: 11,
      2: 9,
      3: 16,
    };
    const pageSizeLength = PAGE_SIZE_MAPPER[props.listingAds.length] || 12;
    const { nextPage, currentPage, pageSize, updatePageInfo } = usePagination({ pageSize: pageSizeLength });
    const aggregations = computed(() => {
      return data.value?.connection?.aggregations;
    });

    const { facets, categories, filters: facetsFilters, clearAll, removeItem, firstLevelCategoryOptions } = useFacets(
      aggregations,
      {
        exclude: ['is_featured_offer', 'is_featured'],
        only: props.includedFacets,
      }
    );

    const sortBy = ref<{ value: ProductAttributeSortInput } | null>();

    const variables = computed<Partial<ProductsQueryVariables>>(() => {
      return {
        page: currentPage.value,
        pageSize: pageSize.value,
        ...(props.type === 'search' ? {} : { sort: sortBy.value?.value || { position: SortEnum.Asc } }),
        filter: filters.value,
        withAggregations: true,
        withPaging: true,
        search: props.search,
      };
    });

    /**
     * a computed watcher for all filters captured from the app context ( props , url , selected options ,or component props )
     *
     * lowest priority goes to the filters captured normally from filter options controllers and url parameters
     * then the container listing hardcoded props like a specific definition like ( specific category , specific brand, ...etc)
     * !this one should be used in a limited and so narrow conditions ,in order not to break the facets selectors functionality or the specific purpose of the listing itself
     * then the hard coded props filters to append other previous values ,
     *
     *
     */
    const filters = computed(() =>
      merge(
        facetsFilters.value,
        props.categoryId
          ? { category_uid: { eq: (process.browser && window.btoa(props.categoryId as string)) ?? '' } }
          : {},
        props.brandId ? { product_brand_id: { eq: props.brandId + '' || '' } } : {},
        props.filters
      )
    );

    const { products, totalCount, data: productData, isFetching } = useProductsGeneric(
      variables,
      props.type ?? 'default',
      {
        newIn: props.type === 'isNew' ? 160 : 0,
      }
    );

    /**
     * controls the display of the facet tags when a filter is applied
     */
    const displayFacetTags = computed(
      () =>
        (options.enableFacetTags &&
          facets.value.filter(
            facet => isFilterableFacet(facet) && enabledFacet(facet, props.renderIncludedFacets ?? [])
          )?.length) ||
        0
    );
    /**
     * if we dont have a replacement empty view
     * or we are currently in the filtering process
     * or we have an empty replacement view, we are not currently filtering and we have no products to display
     */
    const showListingView = computed(() => {
      return (
        !slots.emptyReplacement ||
        !!displayFacetTags.value ||
        (!!slots.emptyReplacement && !displayFacetTags.value && totalCount.value > 0)
      );
    });

    /**
     * called on selected a facet from the facet dropdowns
     * handles updating the current facet with the new selected value
     */
    const updateFacetWithSelectedValue = (value: AggregationOption[], facet: MappedAggregation) => {
      currentPage.value = 1;
      nextTick(() => (facet.value = value));
    };
    const showFacetTags = computed(() => {
      return filters.value && !!Object.keys(filters.value).length;
    });

    watch(productData, value => {
      updatePageInfo(value?.connection?.page_info);
      data.value = value;
    });

    /*
     * called on filter option change dropdown change
     * emits the new filter options to the parent component
     * @param {AggregationOption[]} value
     * */
    watch(facets, value => {
      emit('input', value);
    });

    watch(categories, value => {
      emit('categories', value);
    });

    /*
     * called on filter option change dropdown change
     * emits the new filter options to the parent component
     * @param {AggregationOption[]} value
     * */
    watch(
      () => props.value,
      value => {
        if (!value) return;
        facets.value = value;
      }
    );

    /**
     * Resetting page number when sorting is change
     *
     */
    watch(sortBy, () => {
      currentPage.value = 1;
    });

    function handleRemoveItem(e: any) {
      currentPage.value = 1;
      nextTick(() => removeItem(e));
    }

    return {
      products,
      nextPage,
      totalCount,
      facets,
      sortBy,
      isFiltering,
      showFacetTags,
      displayFacetTags,
      clearAll,
      removeItem,
      updateFacetWithSelectedValue,
      isFetching,
      showListingView,
      enabledFacet,
      disabledFacet,
      // <---
      handleRemoveItem,
      firstLevelCategoryOptions,
    };
  },
});
